import { View, Dialog } from 'web-mojo';
import LocationFormView from '@portal/routes/LocationFormView.js';

/**
 * StopConfigDrawer — add or edit a template stop.
 * Emits 'saved' when the stop is created/updated.
 */
export default class StopConfigDrawer extends View {
    constructor({ template, stop = null, stopCount = 0 }) {
        super();
        this.template_model = template;
        this.stop = stop;        // null = create, object = edit
        this.stopCount = stopCount;

        // loaded data
        this.locations = [];
        this.machines = [];

        // selected state
        this.selectedLocationId = stop?.location?.id || null;
        this.selectedMachineId = stop?.machine?.id || null;
    }

    async onInit() {
        const response = await app.api.get('/api/camaccess/location', {
            group: app.groupId(),
            is_active: 'true',
            size: 200,
            sort: 'name',
        });
        if (response.success && response.data.status) {
            this.locations = response.data.data || [];
        }

        if (this.selectedLocationId) {
            await this._loadMachines(this.selectedLocationId);
        }
    }

    async onActionNewLocation() {
        const form = new LocationFormView();
        Dialog.showDialog({
            title: 'New Location',
            body: form,
            size: 'lg',
            buttons: [
                { text: 'Cancel', class: 'btn-secondary', dismiss: true },
                { text: 'Add Location', class: 'btn-primary',
                  handler: () => form.onActionSave()
                },
            ],
        });
        form.on('saved', async (newLoc) => {
            this.locations.push(newLoc);
            this.selectedLocationId = String(newLoc.id);
            this.selectedMachineId = null;
            await this._loadMachines(newLoc.id);
            this.render();
        });
    }

    async _loadMachines(locationId) {
        const response = await app.api.get('/api/camaccess/machine', {
            location: locationId,
            is_active: 'true',
            size: 200,
        });
        if (response.success && response.data.status) {
            this.machines = response.data.data || [];
        } else {
            this.machines = [];
        }
    }

    async onActionLocationChange(event, el) {
        const locationId = el.value;
        this.selectedLocationId = locationId || null;
        this.selectedMachineId = null;

        if (locationId) {
            await this._loadMachines(locationId);
        } else {
            this.machines = [];
        }

        // Re-render the machine + device sections only
        const machineSection = this.element.querySelector('[data-container="machine-section"]');
        if (machineSection) {
            machineSection.innerHTML = this._machineSectionHtml();
        }

        const warningSection = this.element.querySelector('[data-container="location-warning"]');
        if (warningSection) {
            warningSection.innerHTML = this._locationWarningHtml(locationId);
        }
    }

    onActionMachineChange(event, el) {
        this.selectedMachineId = el.value || null;
        const deviceSection = this.element.querySelector('[data-container="device-section"]');
        if (deviceSection) {
            deviceSection.innerHTML = this._deviceSectionHtml();
        }
    }

    onActionActivityChange(event, el) {
        const activityType = el.value;
        const conditionalSection = this.element.querySelector('[data-container="activity-conditional"]');
        if (conditionalSection) {
            conditionalSection.innerHTML = this._activityConditionalHtml(activityType);
        }
    }

    async onActionSave() {
        const locationId = this.element.querySelector('[name=location]')?.value;
        if (!locationId) {
            app.toast.warning('Location is required');
            return false;
        }

        const activityType = this.element.querySelector('[name=activity_type]')?.value;
        if (!activityType) {
            app.toast.warning('Activity type is required');
            return false;
        }

        const payload = {
            template: this.template_model.get('id'),
            location: parseInt(locationId),
            activity_type: activityType,
            priority: this.element.querySelector('[name=priority]')?.value || 'normal',
            dwell_minutes: parseInt(this.element.querySelector('[name=dwell_minutes]')?.value || '0') || 0,
            earliest_arrival: this.element.querySelector('[name=earliest_arrival]')?.value || null,
            latest_departure: this.element.querySelector('[name=latest_departure]')?.value || null,
            planner_note: this.element.querySelector('[name=planner_note]')?.value?.trim() || null,
            pin_note: this.element.querySelector('[name=pin_note]')?.checked || false,
        };

        const machineId = this.element.querySelector('[name=machine]')?.value;
        if (machineId) payload.machine = parseInt(machineId);

        const deviceId = this.element.querySelector('[name=device]')?.value;
        if (deviceId) payload.device = parseInt(deviceId);

        // Activity-conditional fields
        if (activityType === 'maintenance' || activityType === 'mixed') {
            const checklist = this.element.querySelector('[name=checklist_template]')?.value?.trim();
            if (checklist) payload.checklist_template = checklist;
        }
        if (activityType === 'collection' || activityType === 'mixed') {
            const expected = this.element.querySelector('[name=expected_collection_amount]')?.value?.trim();
            if (expected) payload.expected_collection_amount = expected;
        }

        if (!this.stop) {
            payload.sequence_position = this.stopCount;
        }

        let response;
        if (this.stop) {
            response = await app.api.post(`/api/routes/template_stop/${this.stop.id}`, payload);
        } else {
            response = await app.api.post('/api/routes/template_stop', payload);
        }

        if (!response.success || !response.data.status) {
            app.toast.error(response.data?.error || 'Failed to save stop');
            return false;
        }

        app.toast.success(this.stop ? 'Stop updated' : 'Stop added');
        this.emit('saved', response.data.data);
        return true;
    }

    // ── Partial HTML helpers ─────────────────────────────────────────────────

    _locationWarningHtml(locationId) {
        if (!locationId) return '';
        const loc = this.locations.find(l => String(l.id) === String(locationId));
        if (!loc || (loc.latitude && loc.longitude)) return '';
        return `
            <div class="alert alert-warning py-2 small mb-0 mt-2">
                <i class="bi bi-exclamation-triangle me-1"></i>
                This location has no coordinates — the stop will not appear on the operator's map.
                Edit the location to add them manually.
            </div>
        `;
    }

    _machineSectionHtml() {
        if (!this.selectedLocationId) {
            return '<p class="text-muted small">Select a location first.</p>';
        }
        if (this.machines.length === 0) {
            return '<p class="text-muted small">No machines at this location.</p>';
        }
        const options = this.machines.map(m => {
            const selected = String(m.id) === String(this.selectedMachineId) ? 'selected' : '';
            const label = m.name + (m.machine_type ? ` · ${m.machine_type}` : '');
            return `<option value="${m.id}" ${selected}>${label}</option>`;
        }).join('');

        return `
            <select class="form-select" name="machine" data-action="machine-change">
                <option value="">No specific machine</option>
                ${options}
            </select>
        `;
    }

    _deviceSectionHtml() {
        const machine = this.machines.find(m => String(m.id) === String(this.selectedMachineId));
        const device = machine?.device;

        if (!device) {
            return '<p class="text-muted small">No CamLock linked to the selected machine.</p>';
        }
        return `
            <select class="form-select" name="device">
                <option value="${device.id}" selected>${device.name} (auto-linked)</option>
                <option value="">No lock assignment</option>
            </select>
        `;
    }

    _activityConditionalHtml(activityType) {
        if (!activityType || activityType === 'stocking') {
            return '<p class="text-muted small">ℹ Stocking: parts log not required.</p>';
        }
        const parts = [];
        if (activityType === 'maintenance' || activityType === 'mixed') {
            parts.push(`
                <div class="mb-3">
                    <label class="form-label">Checklist Template</label>
                    <input type="text" class="form-control" name="checklist_template"
                        value="${this.stop?.checklist_template || ''}"
                        placeholder="e.g. Full PM Checklist">
                </div>
            `);
        }
        if (activityType === 'collection' || activityType === 'mixed') {
            parts.push(`
                <div class="mb-3">
                    <label class="form-label">Expected Collection Amount</label>
                    <input type="text" class="form-control" name="expected_collection_amount"
                        value="${this.stop?.expected_collection_amount || ''}"
                        placeholder="e.g. 500.00">
                </div>
            `);
        }
        return parts.join('');
    }

    set template(v) { this._t = v; }

    get template() {
        const s = this.stop;
        const activityType = s?.activity_type || 'stocking';

        const locationOptions = this.locations.map(loc => {
            const selected = String(loc.id) === String(this.selectedLocationId) ? 'selected' : '';
            return `<option value="${loc.id}" ${selected}>${loc.name}</option>`;
        }).join('');

        const priorityOptions = ['normal', 'high', 'urgent'].map(p => {
            const selected = (s?.priority || 'normal') === p ? 'selected' : '';
            return `<option value="${p}" ${selected}>${p.charAt(0).toUpperCase() + p.slice(1)}</option>`;
        }).join('');

        const activityOptions = ['stocking', 'maintenance', 'collection', 'mixed'].map(a => {
            const selected = activityType === a ? 'selected' : '';
            const label = a.charAt(0).toUpperCase() + a.slice(1);
            return `<option value="${a}" ${selected}>${label}</option>`;
        }).join('');

        return `
            <div class="p-3">
                <div class="mb-3">
                    <label class="form-label">Location *</label>
                    <div class="input-group">
                        <select class="form-select" name="location" data-action="location-change">
                            <option value="">Select location…</option>
                            ${locationOptions}
                        </select>
                        <button type="button" class="btn btn-outline-secondary" data-action="new-location"
                            title="Create new location">
                            <i class="bi bi-plus-lg"></i> New
                        </button>
                    </div>
                    <div data-container="location-warning">
                        ${this._locationWarningHtml(this.selectedLocationId)}
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Machine</label>
                    <div data-container="machine-section">
                        ${this._machineSectionHtml()}
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Activity Type *</label>
                    <select class="form-select" name="activity_type" data-action="activity-change">
                        ${activityOptions}
                    </select>
                </div>

                <div data-container="activity-conditional" class="mb-3">
                    ${this._activityConditionalHtml(activityType)}
                </div>

                <div class="row g-3 mb-3">
                    <div class="col-6">
                        <label class="form-label">Earliest Arrival</label>
                        <input type="time" class="form-control" name="earliest_arrival"
                            value="${s?.earliest_arrival || ''}">
                    </div>
                    <div class="col-6">
                        <label class="form-label">Latest Departure</label>
                        <input type="time" class="form-control" name="latest_departure"
                            value="${s?.latest_departure || ''}">
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Dwell Time (minutes)</label>
                    <input type="number" class="form-control" name="dwell_minutes"
                        value="${s?.dwell_minutes || ''}" min="0" placeholder="e.g. 20">
                </div>

                <div class="mb-3">
                    <label class="form-label">Priority</label>
                    <select class="form-select" name="priority">
                        ${priorityOptions}
                    </select>
                </div>

                <div class="mb-3">
                    <label class="form-label">Lock Assignment</label>
                    <div data-container="device-section">
                        ${this._deviceSectionHtml()}
                    </div>
                    <div class="form-text text-muted">
                        <i class="bi bi-shield-check text-success me-1"></i>
                        Access window will be provisioned automatically when the route is scheduled, aligned to the time window above.
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Planner Note</label>
                    <textarea class="form-control" name="planner_note" rows="2"
                        placeholder="e.g. Check coin mech before servicing">${s?.planner_note || ''}</textarea>
                </div>

                <div class="mb-4 form-check">
                    <input type="checkbox" class="form-check-input" name="pin_note" id="pin_note"
                        ${s?.pin_note ? 'checked' : ''}>
                    <label class="form-check-label small" for="pin_note">
                        Pin this note to the stop card
                    </label>
                </div>
            </div>
        `;
    }
}
